import React, { useState, useEffect, useMemo } from 'react';
import {
  Palette,
  Plus,
  Trash2,
  Edit2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Coins,
  Sparkles,
  Upload,
  Search,
  Eye,
  Sliders,
  DollarSign,
  AlertCircle,
  Save,
  Layers,
  Image as ImageIcon,
  X
} from 'lucide-react';
import { roomThemesAPI } from '../utils/api';
import AssetPreview, { resolveAssetUrl } from '../components/AssetPreview';

const DISALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.zip', '.rar', '.exe'];

const RoomThemesManagement = () => {
  const [themes, setThemes] = useState([]);
  const [stats, setStats] = useState({ total: 0, free_count: 0, paid_count: 0, gif_count: 0, active_count: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  // Pricing State for Super Admin Custom Theme uploads
  const [pricing, setPricing] = useState({
    custom_theme_image_price: 1000,
    custom_theme_gif_price: 2500
  });
  const [pricingSaving, setPricingSaving] = useState(false);

  // Modal State for System Themes
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  const [editThemeId, setEditThemeId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    is_free: true,
    price: 0,
    theme_type: 'STATIC',
    sort_order: 0,
    is_active: true,
    file: null,
    existing_image: ''
  });

  const [previewModalUrl, setPreviewModalUrl] = useState(null);
  const [toast, setToast] = useState({ message: '', type: '', visible: false });

  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 3500);
  };

  const fetchThemesAndPricing = async () => {
    setLoading(true);
    try {
      const res = await roomThemesAPI.getAdminThemes();
      if (res.data && res.data.success) {
        setThemes(res.data.data || []);
        if (res.data.stats) {
          setStats(res.data.stats);
        }
        const pricingObj = res.data.pricing || res.data.custom_pricing;
        if (pricingObj) {
          setPricing({
            custom_theme_image_price: pricingObj.custom_theme_image_price ?? pricingObj.image_price ?? pricingObj.imagePrice ?? 1000,
            custom_theme_gif_price: pricingObj.custom_theme_gif_price ?? pricingObj.gif_price ?? pricingObj.gifPrice ?? 2500
          });
        }
      } else {
        showToast(res.data?.message || 'Failed to load themes', 'error');
      }
    } catch (err) {
      console.error('Error fetching themes:', err);
      showToast('Error connecting to room themes API', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThemesAndPricing();
  }, []);

  // Save Custom Upload Pricing
  const handleSavePricing = async (e) => {
    e.preventDefault();
    setPricingSaving(true);
    try {
      const imgVal = parseInt(pricing.custom_theme_image_price) || 0;
      const gifVal = parseInt(pricing.custom_theme_gif_price) || 0;
      const res = await roomThemesAPI.updatePricing({
        custom_theme_image_price: imgVal,
        custom_theme_gif_price: gifVal,
        image_price: imgVal,
        gif_price: gifVal
      });
      if (res.data && res.data.success) {
        const p = res.data.pricing || res.data.data;
        if (p) {
          setPricing({
            custom_theme_image_price: p.custom_theme_image_price ?? p.image_price ?? p.imagePrice ?? imgVal,
            custom_theme_gif_price: p.custom_theme_gif_price ?? p.gif_price ?? p.gifPrice ?? gifVal
          });
        }
        showToast('Custom theme pricing updated successfully', 'success');
      } else {
        showToast(res.data?.message || 'Failed to update pricing', 'error');
      }
    } catch (err) {
      console.error('Error updating pricing:', err);
      const errMsg = err.response?.data?.message || 'Error updating pricing';
      showToast(errMsg, 'error');
    } finally {
      setPricingSaving(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditThemeId(null);
    setModalError('');
    setFormData({
      name: '',
      is_free: true,
      price: 0,
      theme_type: 'STATIC',
      sort_order: themes.length ? Math.max(...themes.map(t => t.sort_order || 0)) + 1 : 0,
      is_active: true,
      file: null,
      existing_image: ''
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (theme) => {
    setEditThemeId(theme.id);
    setModalError('');
    setFormData({
      name: theme.name || '',
      is_free: !!theme.is_free,
      price: theme.price || 0,
      theme_type: theme.theme_type || 'STATIC',
      sort_order: theme.sort_order || 0,
      is_active: theme.is_active !== undefined ? theme.is_active : true,
      file: null,
      existing_image: theme.theme_url || ''
    });
    setIsModalOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const lowerName = file.name.toLowerCase();
    const isDisallowed = DISALLOWED_EXTENSIONS.some(ext => lowerName.endsWith(ext));
    if (isDisallowed) {
      setModalError(`Documents (${file.name.split('.').pop()}) are not allowed. Please select an image or GIF.`);
      return;
    }

    // Auto detect GIF
    const isGif = lowerName.endsWith('.gif') || file.type === 'image/gif';
    setModalError('');
    setFormData(prev => ({
      ...prev,
      file: file,
      theme_type: isGif ? 'GIF' : prev.theme_type
    }));
  };

  const handleSubmitTheme = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setModalError('Theme name is required');
      return;
    }

    if (!editThemeId && !formData.file) {
      setModalError('Please upload a theme image or GIF file');
      return;
    }

    setModalLoading(true);
    setModalError('');

    try {
      const data = new FormData();
      data.append('name', formData.name.trim());
      data.append('is_free', formData.is_free ? 'true' : 'false');
      data.append('price', formData.is_free ? '0' : String(formData.price || 0));
      data.append('theme_type', formData.theme_type);
      data.append('sort_order', formData.sort_order);
      data.append('is_active', formData.is_active ? 'true' : 'false');

      if (formData.file) {
        data.append('file', formData.file);
      }

      let res;
      if (editThemeId) {
        res = await roomThemesAPI.updateTheme(editThemeId, data);
      } else {
        res = await roomThemesAPI.createTheme(data);
      }

      if (res.data && res.data.success) {
        showToast(editThemeId ? 'Theme updated successfully' : 'Theme created successfully', 'success');
        setIsModalOpen(false);
        fetchThemesAndPricing();
      } else {
        setModalError(res.data?.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Error saving theme:', err);
      setModalError(err.response?.data?.message || 'Failed to save theme');
    } finally {
      setModalLoading(false);
    }
  };

  const handleToggleStatus = async (theme) => {
    try {
      const updatedStatus = !theme.is_active;
      const data = new FormData();
      data.append('is_active', updatedStatus ? 'true' : 'false');

      const res = await roomThemesAPI.updateTheme(theme.id, data);
      if (res.data && res.data.success) {
        setThemes(prev => prev.map(t => t.id === theme.id ? { ...t, is_active: updatedStatus } : t));
        setStats(prev => ({
          ...prev,
          active_count: updatedStatus ? prev.active_count + 1 : Math.max(0, prev.active_count - 1)
        }));
        showToast(`Theme ${updatedStatus ? 'activated' : 'deactivated'}`, 'success');
      } else {
        showToast(res.data?.message || 'Failed to update status', 'error');
      }
    } catch (err) {
      console.error('Error toggling theme status:', err);
      showToast('Error toggling theme status', 'error');
    }
  };

  const handleDeleteTheme = async (theme) => {
    if (!window.confirm(`Are you sure you want to delete room theme "${theme.name}"?`)) {
      return;
    }

    try {
      const res = await roomThemesAPI.deleteTheme(theme.id);
      if (res.data && res.data.success) {
        showToast('Theme deleted successfully', 'success');
        fetchThemesAndPricing();
      } else {
        showToast(res.data?.message || 'Failed to delete theme', 'error');
      }
    } catch (err) {
      console.error('Error deleting theme:', err);
      showToast('Error deleting theme', 'error');
    }
  };

  const filteredThemes = useMemo(() => {
    return themes.filter(t => {
      if (activeTab === 'FREE' && !t.is_free) return false;
      if (activeTab === 'PAID' && t.is_free) return false;
      if (activeTab === 'GIF' && t.theme_type !== 'GIF') return false;

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        if (!(t.name || '').toLowerCase().includes(query)) return false;
      }
      return true;
    });
  }, [themes, activeTab, searchQuery]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-xl flex items-center gap-3 text-white font-medium transition-all ${
          toast.type === 'error' ? 'bg-rose-600' : 'bg-emerald-600'
        }`}>
          {toast.type === 'error' ? <XCircle size={20} /> : <CheckCircle2 size={20} />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white shadow-md shadow-purple-100">
            <Palette size={26} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Room Themes Management</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Manage free & paid room themes and configure custom theme upload coin prices for mobile users
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchThemesAndPricing}
            disabled={loading}
            className="p-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            title="Refresh List"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>

          <button
            onClick={handleOpenCreateModal}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl shadow-md shadow-purple-200 font-semibold text-sm transition"
          >
            <Plus size={18} />
            <span>Add System Theme</span>
          </button>
        </div>
      </div>

      {/* Top Card: Super Admin Custom Theme Pricing Configuration */}
      <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 rounded-2xl p-6 text-white shadow-xl border border-indigo-900/50 relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-radial from-purple-500/10 to-transparent pointer-events-none"></div>

        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 relative z-10">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-amber-500/20 text-amber-300 border border-amber-500/30 text-xs font-bold uppercase tracking-wider mb-2">
              <Sparkles size={14} />
              <span>Super Admin Pricing Configuration</span>
            </div>
            <h2 className="text-xl font-bold tracking-tight">User Custom Theme Upload Price (Coins)</h2>
            <p className="text-slate-300 text-sm mt-1 leading-relaxed">
              When a room host uploads their personal room background in the mobile app, the system automatically detects whether it is a Static Image or an Animated GIF and charges the coin amount set below.
            </p>
          </div>

          <form onSubmit={handleSavePricing} className="flex flex-col sm:flex-row items-stretch sm:items-end gap-4 w-full lg:w-auto">
            {/* Image Price */}
            <div className="bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/10 flex-1 sm:w-48">
              <label className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
                <ImageIcon size={14} className="text-blue-400" />
                <span>Static Image Price</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  required
                  value={pricing.custom_theme_image_price}
                  onChange={e => setPricing({ ...pricing, custom_theme_image_price: e.target.value })}
                  className="w-full pl-8 pr-3 py-2 bg-black/40 border border-white/20 rounded-lg text-amber-400 font-extrabold text-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <Coins size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-amber-400" />
              </div>
              <span className="text-[11px] text-slate-400 mt-1 block">Coins per static upload</span>
            </div>

            {/* GIF Price */}
            <div className="bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/10 flex-1 sm:w-48">
              <label className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
                <Sparkles size={14} className="text-pink-400" />
                <span>GIF / Animated Price</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  required
                  value={pricing.custom_theme_gif_price}
                  onChange={e => setPricing({ ...pricing, custom_theme_gif_price: e.target.value })}
                  className="w-full pl-8 pr-3 py-2 bg-black/40 border border-white/20 rounded-lg text-amber-400 font-extrabold text-lg focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <Coins size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-amber-400" />
              </div>
              <span className="text-[11px] text-slate-400 mt-1 block">Coins per GIF/animation upload</span>
            </div>

            {/* Save Button */}
            <button
              type="submit"
              disabled={pricingSaving}
              className="px-5 py-3.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-extrabold rounded-xl shadow-lg shadow-amber-500/20 text-sm transition flex items-center justify-center gap-2 shrink-0 cursor-pointer"
            >
              {pricingSaving ? <RefreshCw size={18} className="animate-spin text-slate-950" /> : <Save size={18} />}
              <span>Save Rates</span>
            </button>
          </form>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Themes</p>
            <p className="text-2xl font-extrabold text-slate-800 mt-1">{stats.total}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center">
            <Layers size={22} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-emerald-500 uppercase tracking-wider">Free Themes</p>
            <p className="text-2xl font-extrabold text-emerald-700 mt-1">{stats.free_count}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <Sparkles size={22} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-amber-500 uppercase tracking-wider">Paid Themes</p>
            <p className="text-2xl font-extrabold text-amber-700 mt-1">{stats.paid_count}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
            <Coins size={22} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-pink-500 uppercase tracking-wider">Animated GIFs</p>
            <p className="text-2xl font-extrabold text-pink-700 mt-1">{stats.gif_count}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-pink-50 text-pink-600 flex items-center justify-center">
            <Palette size={22} />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-indigo-500 uppercase tracking-wider">Active</p>
            <p className="text-2xl font-extrabold text-indigo-700 mt-1">{stats.active_count}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <CheckCircle2 size={22} />
          </div>
        </div>
      </div>

      {/* Tabs and Controls */}
      <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Tabs */}
          <div className="flex p-1 bg-slate-100 rounded-xl w-full sm:w-auto">
            <button
              onClick={() => setActiveTab('ALL')}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                activeTab === 'ALL' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              All Themes
            </button>
            <button
              onClick={() => setActiveTab('FREE')}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                activeTab === 'FREE' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Free Themes (Mobile App)
            </button>
            <button
              onClick={() => setActiveTab('PAID')}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                activeTab === 'PAID' ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Paid Themes
            </button>
            <button
              onClick={() => setActiveTab('GIF')}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                activeTab === 'GIF' ? 'bg-white text-pink-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Animated GIFs
            </button>
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-64">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search room themes..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-slate-700 font-medium"
            />
          </div>
        </div>
      </div>

      {/* Themes Grid */}
      {loading ? (
        <div className="bg-white p-16 rounded-2xl border border-slate-100 shadow-sm text-center">
          <RefreshCw size={36} className="animate-spin text-purple-500 mx-auto mb-3" />
          <p className="text-slate-500 font-medium text-sm">Loading room themes...</p>
        </div>
      ) : filteredThemes.length === 0 ? (
        <div className="bg-white p-16 rounded-2xl border border-slate-100 shadow-sm text-center">
          <Palette size={48} className="text-slate-300 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-slate-700">No Room Themes Found</h3>
          <p className="text-slate-500 text-sm mt-1 max-w-sm mx-auto">
            {searchQuery ? 'No room themes match your search.' : 'Click "Add System Theme" to upload free or coin-based room background themes.'}
          </p>
          <button
            onClick={handleOpenCreateModal}
            className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-semibold inline-flex items-center gap-2"
          >
            <Plus size={16} /> Add System Theme
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {filteredThemes.map(theme => {
            const resolvedImg = resolveAssetUrl(theme.theme_url);
            const isGif = theme.theme_type === 'GIF' || (theme.theme_url && theme.theme_url.toLowerCase().endsWith('.gif'));

            return (
              <div
                key={theme.id}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition group"
              >
                {/* Visual Preview Container */}
                <div className="relative w-full h-48 bg-slate-900 flex items-center justify-center overflow-hidden">
                  {resolvedImg ? (
                    <img
                      src={resolvedImg}
                      alt={theme.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 cursor-pointer"
                      onClick={() => setPreviewModalUrl(resolvedImg)}
                    />
                  ) : (
                    <div className="text-slate-400 flex flex-col items-center">
                      <Palette size={32} />
                      <span className="text-xs mt-1">No Image</span>
                    </div>
                  )}

                  {/* Pricing Badge */}
                  <div className="absolute top-3 left-3 flex gap-1.5">
                    {theme.is_free ? (
                      <span className="px-2.5 py-1 rounded-lg text-xs font-black uppercase tracking-wider bg-emerald-500 text-white shadow-sm">
                        FREE
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-black bg-amber-500 text-slate-950 shadow-sm">
                        <Coins size={12} />
                        <span>{Number(theme.price).toLocaleString()} Coins</span>
                      </span>
                    )}

                    {isGif && (
                      <span className="px-2 py-0.5 rounded-lg text-xs font-black bg-pink-500 text-white shadow-sm">
                        GIF
                      </span>
                    )}
                  </div>

                  {/* Status Toggle Badge */}
                  <div className="absolute top-3 right-3">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold shadow-sm backdrop-blur-md ${
                        theme.is_active ? 'bg-emerald-500/90 text-white' : 'bg-slate-700/90 text-slate-200'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${theme.is_active ? 'bg-white' : 'bg-slate-400'}`}></span>
                      {theme.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {/* Preview Click Overlay */}
                  {resolvedImg && (
                    <button
                      onClick={() => setPreviewModalUrl(resolvedImg)}
                      className="absolute bottom-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-lg opacity-0 group-hover:opacity-100 transition"
                      title="View Full Theme"
                    >
                      <Eye size={16} />
                    </button>
                  )}
                </div>

                {/* Content */}
                <div className="p-4 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-bold text-slate-800 text-base line-clamp-1">{theme.name}</h3>
                      <span className="text-xs font-semibold px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                        #{theme.sort_order}
                      </span>
                    </div>

                    <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                      <span>Format:</span>
                      <span className="font-semibold text-slate-700 uppercase">
                        {theme.theme_type || 'STATIC'}
                      </span>
                    </div>
                  </div>

                  {/* Actions Bar */}
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                    <button
                      onClick={() => handleToggleStatus(theme)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-xl transition ${
                        theme.is_active
                          ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                          : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                      }`}
                    >
                      {theme.is_active ? 'Deactivate' : 'Activate'}
                    </button>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOpenEditModal(theme)}
                        className="p-1.5 text-slate-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition"
                        title="Edit Theme"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteTheme(theme)}
                        className="p-1.5 text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                        title="Delete Theme"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit Theme Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-hidden">
          <div className="bg-white rounded-3xl max-w-xl w-full max-h-[92vh] shadow-2xl border border-slate-100 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between shrink-0 bg-white">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-slate-900">
                  {editThemeId ? 'Edit Room Theme' : 'Add System Room Theme'}
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  Upload room background theme for mobile app hosts. Free themes appear in the free picker.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition cursor-pointer"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Scrollable Form Body */}
            <form onSubmit={handleSubmitTheme} className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <div className="p-6 overflow-y-auto space-y-4 flex-1">
                {modalError && (
                  <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-xl flex items-center gap-2">
                    <AlertCircle size={16} className="shrink-0" />
                    <span>{modalError}</span>
                  </div>
                )}

                {/* Name */}
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Theme Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Neon Cyberpunk Club"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className="w-full mt-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-slate-700 font-semibold"
                  />
                </div>

                {/* Free vs Paid Toggle */}
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Pricing Type</label>
                  <div className="grid grid-cols-2 gap-3 mt-1.5">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, is_free: true, price: 0 })}
                      className={`p-2.5 rounded-xl border text-xs sm:text-sm font-bold flex items-center justify-center gap-2 transition cursor-pointer ${
                        formData.is_free
                          ? 'border-emerald-600 bg-emerald-50/70 text-emerald-700 ring-2 ring-emerald-500/20'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <Sparkles size={16} />
                      <span>Free Theme (0 Coins)</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, is_free: false })}
                      className={`p-2.5 rounded-xl border text-xs sm:text-sm font-bold flex items-center justify-center gap-2 transition cursor-pointer ${
                        !formData.is_free
                          ? 'border-amber-600 bg-amber-50/70 text-amber-700 ring-2 ring-amber-500/20'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <Coins size={16} />
                      <span>Paid (Coin Store)</span>
                    </button>
                  </div>
                </div>

                {/* Price (if paid) & Format */}
                <div className="grid grid-cols-2 gap-3">
                  {!formData.is_free ? (
                    <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Coin Price</label>
                      <input
                        type="number"
                        min="1"
                        required
                        placeholder="e.g. 500"
                        value={formData.price}
                        onChange={e => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
                        className="w-full mt-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-amber-600 focus:outline-none"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Price</label>
                      <input
                        type="text"
                        disabled
                        value="Free for all hosts"
                        className="w-full mt-1 px-4 py-2 bg-slate-100 border border-slate-200 rounded-xl text-sm font-semibold text-emerald-600 cursor-not-allowed"
                      />
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Theme Format</label>
                    <select
                      value={formData.theme_type}
                      onChange={e => setFormData({ ...formData, theme_type: e.target.value })}
                      className="w-full mt-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:outline-none"
                    >
                      <option value="STATIC">Static Image (JPG/PNG/WebP)</option>
                      <option value="GIF">Animated GIF</option>
                    </select>
                  </div>
                </div>

                {/* File Upload */}
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Theme Media File {editThemeId ? '(Leave empty to keep existing)' : '*'}
                  </label>
                  
                  <div className="mt-1.5 p-3.5 border-2 border-dashed border-slate-300 rounded-2xl bg-slate-50 text-center hover:bg-slate-100 transition relative">
                    <input
                      type="file"
                      accept="image/*,.gif,.svga,.webp,.svg,.apng"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex flex-col items-center justify-center gap-1">
                      <Upload size={22} className="text-purple-600" />
                      <span className="text-xs font-bold text-slate-700">
                        {formData.file ? formData.file.name : 'Click or drag theme file here to upload'}
                      </span>
                      <span className="text-[11px] text-slate-400">
                        Supports JPG, PNG, GIF, WebP, SVG, SVGA, APNG
                      </span>
                    </div>
                  </div>

                  {/* Previews */}
                  {(formData.file || formData.existing_image) && (
                    <div className="mt-2.5 p-2.5 bg-slate-100 rounded-xl flex items-center gap-3">
                      <div className="w-14 h-12 bg-slate-900 rounded-lg overflow-hidden flex items-center justify-center shrink-0">
                        <img
                          src={formData.file ? URL.createObjectURL(formData.file) : resolveAssetUrl(formData.existing_image)}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="text-xs min-w-0">
                        <p className="font-bold text-slate-700 truncate">
                          {formData.file ? 'Selected New File' : 'Current Theme File'}
                        </p>
                        <p className="text-slate-500 text-[11px] truncate">
                          {formData.file ? `${(formData.file.size / 1024).toFixed(1)} KB` : formData.existing_image}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sort Order */}
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Display Sort Order</label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={e => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-full mt-1 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:outline-none"
                  />
                </div>

                {/* Active Toggle */}
                <div className="flex items-center gap-3 pt-1">
                  <input
                    type="checkbox"
                    id="theme_is_active"
                    checked={formData.is_active}
                    onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 cursor-pointer"
                  />
                  <label htmlFor="theme_is_active" className="text-sm font-bold text-slate-700 cursor-pointer select-none">
                    Activate theme immediately for mobile users
                  </label>
                </div>
              </div>

              {/* Sticky Modal Footer */}
              <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3 shrink-0 rounded-b-3xl">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={modalLoading}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm font-bold hover:bg-slate-100 transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={modalLoading}
                  className="px-6 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-bold shadow-md shadow-purple-200 hover:from-purple-700 hover:to-pink-700 transition flex items-center gap-2 cursor-pointer"
                >
                  {modalLoading && <RefreshCw size={16} className="animate-spin" />}
                  <span>{editThemeId ? 'Save Changes' : 'Upload Theme'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Full Preview Modal */}
      {previewModalUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6"
          onClick={() => setPreviewModalUrl(null)}
        >
          <div className="relative max-w-3xl max-h-[85vh] bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-slate-700">
            <button
              onClick={() => setPreviewModalUrl(null)}
              className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black/90 text-white rounded-full transition z-10"
            >
              <XCircle size={22} />
            </button>
            <img
              src={previewModalUrl}
              alt="Full Preview"
              className="w-full h-auto max-h-[80vh] object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomThemesManagement;
